import { useState, useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Drawer,
  IconButton,
  Box,
  Typography,
  Divider,
  Badge,
  Popover,
  Chip,
  Tooltip
} from '@mui/material';
import SupportAgentRoundedIcon from '@mui/icons-material/SupportAgentRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import NotificationsRoundedIcon from '@mui/icons-material/NotificationsRounded';
import NotificationsActiveRoundedIcon from '@mui/icons-material/NotificationsActiveRounded';
import NotificationsNoneRoundedIcon from '@mui/icons-material/NotificationsNoneRounded';
import HourglassEmptyRoundedIcon from '@mui/icons-material/HourglassEmptyRounded';
import PendingActionsRoundedIcon from '@mui/icons-material/PendingActionsRounded';
import DoneAllRoundedIcon from '@mui/icons-material/DoneAllRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import { PageHeader, FilterBar, EmptyState, StatusChip } from '../components/ui';
import CreateSupportTicketDialog from '../components/CreateSupportTicketDialog';
import { crmAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import useListQueryParams from '../hooks/useListQueryParams';
import { statusChipColors } from '../theme';
import './ModuleAdmin.css';

const STATUS_OPTIONS = [
  { value: 'open', label: 'Open' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'on_hold', label: 'On Hold' },
  { value: 'closed', label: 'Closed' }
];

function formatDate(createdAt) {
  if (!createdAt) return '—';
  const d = new Date(createdAt);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

function formatTimeAgo(timestamp) {
  if (!timestamp) return 'Just now';
  const diffSec = Math.max(0, Math.floor((Date.now() - new Date(timestamp).getTime()) / 1000));
  if (diffSec < 60) return 'Just now';
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHour = Math.floor(diffMin / 60);
  if (diffHour < 24) return `${diffHour}h ago`;
  const diffDay = Math.floor(diffHour / 24);
  return `${diffDay}d ago`;
}

function statusLabel(status) {
  return STATUS_OPTIONS.find((o) => o.value === status)?.label || status || 'Open';
}

/** Gentle notification sound via Web Audio API (D5 -> A5 chime) */
function playChime() {
  try {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    if (ctx.state === 'suspended') {
      ctx.resume().catch(() => {});
    }
    const t = ctx.currentTime;

    const o1 = ctx.createOscillator();
    const g1 = ctx.createGain();
    o1.type = 'sine';
    o1.frequency.setValueAtTime(587.33, t);
    g1.gain.setValueAtTime(0.08, t);
    g1.gain.exponentialRampToValueAtTime(0.001, t + 0.28);
    o1.connect(g1);
    g1.connect(ctx.destination);
    o1.start(t);
    o1.stop(t + 0.28);

    const o2 = ctx.createOscillator();
    const g2 = ctx.createGain();
    o2.type = 'sine';
    o2.frequency.setValueAtTime(880, t + 0.12);
    g2.gain.setValueAtTime(0.1, t + 0.12);
    g2.gain.exponentialRampToValueAtTime(0.001, t + 0.48);
    o2.connect(g2);
    g2.connect(ctx.destination);
    o2.start(t + 0.12);
    o2.stop(t + 0.48);
  } catch {
    // Audio autoplay restrictions or no audio device
  }
}

const SupportTickets = () => {
  const queryClient = useQueryClient();
  const { user, can } = useAuth();
  const { showToast } = useToast();
  const canManage = can('lead_requests_manage');
  const canCreate = can('lead_requests_create') || canManage;
  const [createOpen, setCreateOpen] = useState(false);
  const [viewTicket, setViewTicket] = useState(null);

  // Notification Popover state
  const [notifAnchorEl, setNotifAnchorEl] = useState(null);
  const notifOpen = Boolean(notifAnchorEl);

  const notifStorageKey = user?.id ? `support_ticket_notifs_${user.id}` : null;
  const seenStorageKey = user?.id ? `support_ticket_seen_${user.id}` : null;

  const [notifications, setNotifications] = useState(() => {
    if (!user?.id) return [];
    try {
      const raw = localStorage.getItem(`support_ticket_notifs_${user.id}`);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    if (!notifStorageKey) return;
    try {
      const raw = localStorage.getItem(notifStorageKey);
      setNotifications(raw ? JSON.parse(raw) : []);
    } catch {
      setNotifications([]);
    }
  }, [notifStorageKey]);

  const unreadCount = notifications.filter((n) => !n.isRead).length;

  const markAllRead = () => {
    setNotifications((prev) => {
      const updated = prev.map((n) => ({ ...n, isRead: true }));
      if (notifStorageKey) {
        try {
          localStorage.setItem(notifStorageKey, JSON.stringify(updated));
        } catch {}
      }
      return updated;
    });
  };

  const clearAllNotifs = () => {
    setNotifications([]);
    if (notifStorageKey) {
      try {
        localStorage.removeItem(notifStorageKey);
      } catch {}
    }
  };

  const handleNotificationClick = (item) => {
    setNotifications((prev) => {
      const updated = prev.map((n) => (n.id === item.id ? { ...n, isRead: true } : n));
      if (notifStorageKey) {
        try {
          localStorage.setItem(notifStorageKey, JSON.stringify(updated));
        } catch {}
      }
      return updated;
    });
    setViewTicket(item);
    setNotifAnchorEl(null);
  };

  const { query, setQuery } = useListQueryParams({
    page: '1',
    limit: '25',
    status: '',
    search: '',
    assignedTo: ''
  });

  const page = Number(query.page || 1);
  const limit = Number(query.limit || 25);
  const statusFilter = query.status || '';
  const search = query.search || '';
  const assignedTo = query.assignedTo || '';

  const { data: assigneesRes } = useQuery(['crm-assignees'], () => crmAPI.getAssignees(), {
    staleTime: 60_000
  });
  const assignees = assigneesRes?.data?.data || [];
  const selectedAssignee = assignees.find((a) => String(a.id) === String(assignedTo));
  const assigneeLabel = selectedAssignee
    ? [selectedAssignee.firstName, selectedAssignee.lastName].filter(Boolean).join(' ').trim() || selectedAssignee.name || ''
    : '';

  // Main tickets query (polls every 12 seconds so stats & table remain fresh)
  const { data, isLoading } = useQuery(
    ['support-tickets', statusFilter, search, assignedTo, page, limit],
    () =>
      crmAPI.listSupportTickets({
        page,
        limit,
        status: statusFilter || undefined,
        search: search || undefined,
        assignedTo: assignedTo || undefined
      }),
    {
      refetchInterval: 12_000,
      staleTime: 6_000
    }
  );

  const tickets = data?.data?.data || [];
  const pagination = data?.data?.pagination || {
    page: 1,
    totalPages: 1,
    total: tickets.length
  };

  const stats = data?.data?.stats || {
    open: tickets.filter((t) => t.status === 'open').length,
    inProgress: tickets.filter((t) => t.status === 'in_progress').length,
    closed: tickets.filter((t) => t.status === 'closed').length,
    total: pagination.total || tickets.length
  };

  const userStats = data?.data?.userStats || {
    open: tickets.filter((t) => t.assignedTo === user?.id && t.status === 'open').length,
    inProgress: tickets.filter((t) => t.assignedTo === user?.id && t.status === 'in_progress').length,
    closed: tickets.filter((t) => t.assignedTo === user?.id && t.status === 'closed').length,
    total: tickets.filter((t) => t.assignedTo === user?.id).length
  };

  // Live polling for tickets assigned to currently logged-in admin user
  const { data: myAssignedRes } = useQuery(
    ['support-tickets-assigned-live', user?.id],
    () => crmAPI.listSupportTickets({ assignedTo: user?.id, limit: 30 }),
    {
      enabled: !!user?.id,
      refetchInterval: 10_000,
      staleTime: 5_000
    }
  );

  const isFirstRunRef = useRef(true);

  useEffect(() => {
    if (!user?.id || !myAssignedRes?.data?.data || !seenStorageKey) return;
    const currentAssigned = myAssignedRes.data.data;

    let seenIds = new Set();
    try {
      const stored = localStorage.getItem(seenStorageKey);
      if (stored) {
        seenIds = new Set(JSON.parse(stored));
      }
    } catch {}

    if (isFirstRunRef.current) {
      isFirstRunRef.current = false;
      currentAssigned.forEach((t) => seenIds.add(t.id));
      try {
        localStorage.setItem(seenStorageKey, JSON.stringify([...seenIds]));
      } catch {}
      return;
    }

    const newlyAssigned = currentAssigned.filter((t) => !seenIds.has(t.id));
    if (newlyAssigned.length > 0) {
      playChime();

      const latest = newlyAssigned[0];
      showToast({
        message:
          newlyAssigned.length === 1
            ? `🔔 Ticket #${latest.id} assigned to you: ${latest.name}`
            : `🔔 ${newlyAssigned.length} new support tickets assigned to you!`,
        severity: 'info'
      });

      const newNotifs = newlyAssigned.map((t) => ({
        id: t.id,
        name: t.name,
        phone: t.phone,
        eventTitle: t.eventTitle || t.eventSlug,
        reason: t.reason,
        comment: t.comment,
        status: t.status,
        assignedTo: t.assignedTo,
        assignedToName: t.assignedToName,
        assignedAt: Date.now(),
        isRead: false
      }));

      setNotifications((prev) => {
        const existingIds = new Set(prev.map((p) => p.id));
        const filteredNew = newNotifs.filter((n) => !existingIds.has(n.id));
        const merged = [...filteredNew, ...prev].slice(0, 50);
        if (notifStorageKey) {
          try {
            localStorage.setItem(notifStorageKey, JSON.stringify(merged));
          } catch {}
        }
        return merged;
      });

      newlyAssigned.forEach((t) => seenIds.add(t.id));
      try {
        localStorage.setItem(seenStorageKey, JSON.stringify([...seenIds]));
      } catch {}

      queryClient.invalidateQueries('support-tickets');
    }
  }, [myAssignedRes, user?.id, seenStorageKey, notifStorageKey]);

  const updateMutation = useMutation(
    ({ id, ...payload }) => crmAPI.updateSupportTicket(id, payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('support-tickets');
        queryClient.invalidateQueries(['support-tickets-assigned-live', user?.id]);
        showToast({ message: 'Support ticket updated.', severity: 'success' });
      },
      onError: (err) =>
        showToast({
          message: err.response?.data?.message || 'Failed to update ticket',
          severity: 'error'
        })
    }
  );

  return (
    <div className="module-page">
      <PageHeader
        title="Support Tickets"
        subtitle="Internal CRM support tickets — assign staff and track status."
        actions={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {/* Notification Bell Button */}
            <Tooltip
              title={
                unreadCount > 0
                  ? `${unreadCount} unread notification${unreadCount > 1 ? 's' : ''}`
                  : 'Ticket Notifications'
              }
            >
              <IconButton
                onClick={(e) => setNotifAnchorEl(e.currentTarget)}
                sx={{
                  bgcolor: '#fff',
                  border: '1px solid #e2e8f0',
                  borderRadius: '10px',
                  p: '8px 10px',
                  boxShadow: '0 2px 6px rgba(15, 23, 42, 0.05)',
                  transition: 'all 0.15s ease',
                  '&:hover': {
                    bgcolor: '#f8fafc',
                    borderColor: '#cbd5e1',
                    transform: 'translateY(-1px)'
                  }
                }}
              >
                <Badge
                  badgeContent={unreadCount}
                  max={99}
                  sx={{
                    '& .MuiBadge-badge': {
                      bgcolor: '#de5824',
                      color: '#fff',
                      fontWeight: 700,
                      fontSize: '0.72rem',
                      minWidth: '18px',
                      height: '18px',
                      padding: '0 4px',
                      boxShadow: '0 0 0 2px #fff',
                      ...(unreadCount > 0
                        ? {
                            animation: 'pulseBadge 2s infinite'
                          }
                        : {})
                    }
                  }}
                >
                  {unreadCount > 0 ? (
                    <NotificationsActiveRoundedIcon sx={{ color: '#de5824', fontSize: '1.35rem' }} />
                  ) : (
                    <NotificationsRoundedIcon sx={{ color: '#64748b', fontSize: '1.35rem' }} />
                  )}
                </Badge>
              </IconButton>
            </Tooltip>

            {canCreate ? (
              <Button
                variant="contained"
                startIcon={<SupportAgentRoundedIcon />}
                onClick={() => setCreateOpen(true)}
              >
                New ticket
              </Button>
            ) : null}
          </Box>
        }
      />

      {/* Top Metric Cards: Open, In Progress, and Assigned to You */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(3, 1fr)'
          },
          gap: 2,
          mb: 2.5
        }}
      >
        {/* Open Tickets Card */}
        <Box
          onClick={() => {
            setQuery({
              status: statusFilter === 'open' ? '' : 'open',
              page: '1'
            });
          }}
          sx={{
            bgcolor: '#fff',
            borderRadius: '16px',
            p: 2.25,
            border: '1px solid',
            borderColor: statusFilter === 'open' ? '#f59e0b' : '#e2e8f0',
            boxShadow:
              statusFilter === 'open'
                ? '0 8px 24px rgba(245, 158, 11, 0.15)'
                : '0 2px 10px rgba(15, 23, 42, 0.04)',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            position: 'relative',
            overflow: 'hidden',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: '0 8px 24px rgba(15, 23, 42, 0.08)',
              borderColor: statusFilter === 'open' ? '#f59e0b' : '#cbd5e1'
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              bgcolor: '#f59e0b'
            }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
            <Typography
              variant="caption"
              sx={{ fontWeight: 700, color: '#64748b', letterSpacing: '0.04em', textTransform: 'uppercase' }}
            >
              Open Tickets
            </Typography>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '12px',
                bgcolor: '#fffbeb',
                color: '#d97706',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <HourglassEmptyRoundedIcon sx={{ fontSize: 22 }} />
            </Box>
          </Box>
          <Typography variant="h4" sx={{ fontWeight: 800, color: '#0f172a', mb: 0.5, lineHeight: 1 }}>
            {stats.open}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1 }}>
            <Typography variant="caption" color="text.secondary">
              {assigneeLabel ? `Awaiting resolution · ${assigneeLabel}` : 'Awaiting resolution'}
            </Typography>
            {statusFilter === 'open' && (
              <Chip
                label="Filtered"
                size="small"
                sx={{ height: 20, fontSize: '0.68rem', fontWeight: 700, bgcolor: '#fef3c7', color: '#92400e' }}
              />
            )}
          </Box>
        </Box>

        {/* In Progress Tickets Card */}
        <Box
          onClick={() => {
            setQuery({
              status: statusFilter === 'in_progress' ? '' : 'in_progress',
              page: '1'
            });
          }}
          sx={{
            bgcolor: '#fff',
            borderRadius: '16px',
            p: 2.25,
            border: '1px solid',
            borderColor: statusFilter === 'in_progress' ? '#3b82f6' : '#e2e8f0',
            boxShadow:
              statusFilter === 'in_progress'
                ? '0 8px 24px rgba(59, 130, 246, 0.15)'
                : '0 2px 10px rgba(15, 23, 42, 0.04)',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            position: 'relative',
            overflow: 'hidden',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: '0 8px 24px rgba(15, 23, 42, 0.08)',
              borderColor: statusFilter === 'in_progress' ? '#3b82f6' : '#cbd5e1'
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              bgcolor: '#3b82f6'
            }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
            <Typography
              variant="caption"
              sx={{ fontWeight: 700, color: '#64748b', letterSpacing: '0.04em', textTransform: 'uppercase' }}
            >
              In Progress
            </Typography>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '12px',
                bgcolor: '#eff6ff',
                color: '#2563eb',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <PendingActionsRoundedIcon sx={{ fontSize: 22 }} />
            </Box>
          </Box>
          <Typography variant="h4" sx={{ fontWeight: 800, color: '#0f172a', mb: 0.5, lineHeight: 1 }}>
            {stats.inProgress}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1 }}>
            <Typography variant="caption" color="text.secondary">
              {assigneeLabel ? `Being handled by ${assigneeLabel}` : 'Being handled by team'}
            </Typography>
            {statusFilter === 'in_progress' && (
              <Chip
                label="Filtered"
                size="small"
                sx={{ height: 20, fontSize: '0.68rem', fontWeight: 700, bgcolor: '#dbeafe', color: '#1e40af' }}
              />
            )}
          </Box>
        </Box>

        {/* Assigned To You Card */}
        <Box
          onClick={() => {
            if (!user?.id) return;
            setQuery({
              assignedTo: assignedTo === String(user.id) ? '' : String(user.id),
              page: '1'
            });
          }}
          sx={{
            bgcolor: '#fff',
            borderRadius: '16px',
            p: 2.25,
            border: '1px solid',
            borderColor: assignedTo === String(user?.id) ? '#de5824' : '#e2e8f0',
            boxShadow:
              assignedTo === String(user?.id)
                ? '0 8px 24px rgba(222, 88, 36, 0.15)'
                : '0 2px 10px rgba(15, 23, 42, 0.04)',
            cursor: user?.id ? 'pointer' : 'default',
            transition: 'all 0.2s ease',
            position: 'relative',
            overflow: 'hidden',
            '&:hover': user?.id
              ? {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 24px rgba(15, 23, 42, 0.08)',
                  borderColor: assignedTo === String(user?.id) ? '#de5824' : '#cbd5e1'
                }
              : {},
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              bgcolor: '#de5824'
            }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
            <Typography
              variant="caption"
              sx={{ fontWeight: 700, color: '#64748b', letterSpacing: '0.04em', textTransform: 'uppercase' }}
            >
              Assigned To You
            </Typography>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '12px',
                bgcolor: '#fff7ed',
                color: '#de5824',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <SupportAgentRoundedIcon sx={{ fontSize: 22 }} />
            </Box>
          </Box>
          <Typography variant="h4" sx={{ fontWeight: 800, color: '#0f172a', mb: 0.5, lineHeight: 1 }}>
            {userStats.open + userStats.inProgress}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1 }}>
            <Typography variant="caption" color="text.secondary">
              {userStats.open} open · {userStats.inProgress} in progress
            </Typography>
            {assignedTo === String(user?.id) && (
              <Chip
                label="Filtered"
                size="small"
                sx={{ height: 20, fontSize: '0.68rem', fontWeight: 700, bgcolor: '#ffedd5', color: '#c2410c' }}
              />
            )}
          </Box>
        </Box>
      </Box>

      {/* Filter Row */}
      <FilterBar>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            label="Status"
            onChange={(e) => setQuery({ status: e.target.value, page: '1' })}
          >
            <MenuItem value="">All</MenuItem>
            {STATUS_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Assignee</InputLabel>
          <Select
            value={assignedTo}
            label="Assignee"
            onChange={(e) => setQuery({ assignedTo: e.target.value, page: '1' })}
          >
            <MenuItem value="">All</MenuItem>
            {assignees.map((u) => (
              <MenuItem key={u.id} value={String(u.id)}>
                {u.name || u.email || `User #${u.id}`}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          size="small"
          placeholder="Search name, phone, event, reason"
          value={search}
          onChange={(e) => setQuery({ search: e.target.value, page: '1' })}
          sx={{ minWidth: 280 }}
        />
      </FilterBar>

      <div className="module-card">
        {isLoading ? (
          <p className="module-loading">Loading support tickets…</p>
        ) : tickets.length === 0 ? (
          <EmptyState
            title="No support tickets yet"
            description="Create a ticket from Lead Requests or use New ticket above."
          />
        ) : (
          <table className="module-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Name</th>
                <th>Phone</th>
                <th>Event</th>
                <th>Reason</th>
                <th>Assignee</th>
                <th>Status</th>
                <th style={{ textAlign: 'center' }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {tickets.map((row) => (
                <tr key={row.id}>
                  <td>#{row.id}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>{row.name || '—'}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>{row.phone || '—'}</td>
                  <td>{row.eventTitle || row.eventSlug || '—'}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>{row.reason || '—'}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {canManage ? (
                      <Select
                        size="small"
                        displayEmpty
                        value={row.assignedTo != null ? String(row.assignedTo) : ''}
                        disabled={updateMutation.isLoading}
                        onChange={(e) =>
                          updateMutation.mutate({
                            id: row.id,
                            assignedTo: e.target.value === '' ? null : Number(e.target.value)
                          })
                        }
                        sx={{ minWidth: 160 }}
                      >
                        <MenuItem value="">Unassigned</MenuItem>
                        {assignees.map((u) => (
                          <MenuItem key={u.id} value={String(u.id)}>
                            {u.name || u.email || `User #${u.id}`}
                          </MenuItem>
                        ))}
                      </Select>
                    ) : (
                      row.assignedToName || '—'
                    )}
                  </td>
                  <td>
                    {canManage ? (
                      <Select
                        size="small"
                        value={row.status || 'open'}
                        disabled={updateMutation.isLoading}
                        onChange={(e) =>
                          updateMutation.mutate({ id: row.id, status: e.target.value })
                        }
                        sx={{
                          minWidth: 125,
                          backgroundColor: statusChipColors[row.status]?.bg || '#f1f5f9',
                          color: statusChipColors[row.status]?.text || '#475569',
                          borderRadius: '16px',
                          fontWeight: 600,
                          fontSize: '0.80rem',
                          height: '28px',
                          '.MuiOutlinedInput-notchedOutline': {
                            borderColor: statusChipColors[row.status]?.border || '#cbd5e1',
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: statusChipColors[row.status]?.border || '#cbd5e1',
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: statusChipColors[row.status]?.border || '#cbd5e1',
                          },
                          '.MuiSelect-select': {
                            paddingTop: '2px',
                            paddingBottom: '2px',
                            paddingLeft: '10px'
                          },
                          '.MuiSvgIcon-root': {
                            color: statusChipColors[row.status]?.text || '#475569'
                          }
                        }}
                      >
                        {STATUS_OPTIONS.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </Select>
                    ) : (
                      <StatusChip status={row.status || 'open'} label={statusLabel(row.status)} />
                    )}
                  </td>
                  <td align="center">
                    <IconButton size="small" onClick={() => setViewTicket(row)} sx={{ border: '1px solid #e2e8f0' }}>
                      <VisibilityRoundedIcon fontSize="small" color="action" />
                    </IconButton>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {pagination.totalPages > 1 && (
          <div className="module-list-pagination">
            <span>
              Page {pagination.page} of {pagination.totalPages} ({pagination.total} total)
            </span>
            <div className="module-list-pagination-btns">
              <button
                type="button"
                className="module-btn secondary"
                disabled={page <= 1}
                onClick={() => setQuery({ page: String(page - 1) }, { resetPage: false })}
              >
                Previous
              </button>
              <button
                type="button"
                className="module-btn secondary"
                disabled={page >= pagination.totalPages}
                onClick={() => setQuery({ page: String(page + 1) }, { resetPage: false })}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Notifications Popover */}
      <Popover
        open={notifOpen}
        anchorEl={notifAnchorEl}
        onClose={() => setNotifAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            width: 380,
            maxWidth: 'calc(100vw - 32px)',
            mt: 1,
            borderRadius: '16px',
            boxShadow: '0 16px 40px rgba(15, 23, 42, 0.14), 0 0 0 1px rgba(226, 232, 240, 0.8)',
            overflow: 'hidden'
          }
        }}
      >
        <Box sx={{ p: 2, pb: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #f1f5f9' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="subtitle1" fontWeight={700} color="#0f172a">
              Notifications
            </Typography>
            {unreadCount > 0 && (
              <Chip
                label={`${unreadCount} new`}
                size="small"
                sx={{
                  height: 20,
                  fontSize: '0.7rem',
                  fontWeight: 700,
                  bgcolor: '#fff7ed',
                  color: '#c2410c',
                  border: '1px solid #ffedd5'
                }}
              />
            )}
          </Box>
          {notifications.length > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {unreadCount > 0 && (
                <Button
                  size="small"
                  startIcon={<DoneAllRoundedIcon sx={{ fontSize: 16 }} />}
                  onClick={markAllRead}
                  sx={{ fontSize: '0.75rem', textTransform: 'none', py: 0.25, px: 0.75, color: '#475569' }}
                >
                  Mark all read
                </Button>
              )}
              <IconButton size="small" onClick={clearAllNotifs} title="Clear notifications" sx={{ color: '#94a3b8' }}>
                <DeleteOutlineRoundedIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>
          )}
        </Box>

        <Box sx={{ maxHeight: 380, overflowY: 'auto' }}>
          {notifications.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <NotificationsNoneRoundedIcon sx={{ fontSize: 44, color: '#cbd5e1', mb: 1 }} />
              <Typography variant="body2" fontWeight={600} color="#334155">
                No notifications yet
              </Typography>
              <Typography variant="caption" color="#94a3b8">
                When tickets are assigned to you, you will be notified here in real-time.
              </Typography>
            </Box>
          ) : (
            notifications.map((item) => (
              <Box
                key={item.id}
                onClick={() => handleNotificationClick(item)}
                sx={{
                  p: 1.75,
                  px: 2,
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1.5,
                  cursor: 'pointer',
                  borderBottom: '1px solid #f8fafc',
                  bgcolor: item.isRead ? '#ffffff' : '#fff7ed',
                  transition: 'background-color 0.15s ease',
                  '&:hover': {
                    bgcolor: item.isRead ? '#f8fafc' : '#ffedd5'
                  }
                }}
              >
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: '10px',
                    bgcolor: item.isRead ? '#f1f5f9' : '#fed7aa',
                    color: item.isRead ? '#64748b' : '#c2410c',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    mt: 0.25
                  }}
                >
                  <SupportAgentRoundedIcon sx={{ fontSize: 20 }} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.25 }}>
                    <Typography variant="body2" fontWeight={item.isRead ? 600 : 700} color="#0f172a" noWrap>
                      Ticket #{item.id} — {item.name}
                    </Typography>
                    {!item.isRead && (
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#de5824', flexShrink: 0 }} />
                    )}
                  </Box>
                  <Typography variant="caption" color="#64748b" sx={{ display: 'block', mb: 0.5 }} noWrap>
                    {item.eventTitle || item.reason || 'Support Request'}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                    <StatusChip status={item.status || 'open'} label={statusLabel(item.status)} />
                    <Typography variant="caption" color="#94a3b8" sx={{ fontSize: '0.72rem' }}>
                      {formatTimeAgo(item.assignedAt)}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            ))
          )}
        </Box>

        {user?.id ? (
          <Box sx={{ p: 1.25, px: 2, bgcolor: '#f8fafc', borderTop: '1px solid #f1f5f9', textAlign: 'center' }}>
            <Button
              size="small"
              fullWidth
              onClick={() => {
                setQuery({ assignedTo: String(user.id), page: '1' });
                setNotifAnchorEl(null);
              }}
              sx={{
                textTransform: 'none',
                fontSize: '0.8rem',
                fontWeight: 600,
                color: '#de5824'
              }}
            >
              View all tickets assigned to you →
            </Button>
          </Box>
        ) : null}
      </Popover>

      <CreateSupportTicketDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSaved={() => {
          setCreateOpen(false);
          queryClient.invalidateQueries('support-tickets');
          showToast({ message: 'Support ticket created.', severity: 'success' });
        }}
      />

      <Drawer anchor="right" open={!!viewTicket} onClose={() => setViewTicket(null)}>
        {viewTicket ? (
          <Box sx={{ width: { xs: 300, sm: 400 }, p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6" fontWeight={700}>Ticket #{viewTicket.id}</Typography>
              <IconButton size="small" onClick={() => setViewTicket(null)}>
                <CloseRoundedIcon />
              </IconButton>
            </Box>
            
            <Typography variant="subtitle2" color="text.secondary">Name</Typography>
            <Typography variant="body2" sx={{ mb: 2, fontWeight: 500 }}>{viewTicket.name || '—'}</Typography>
            
            <Typography variant="subtitle2" color="text.secondary">Phone</Typography>
            <Typography variant="body2" sx={{ mb: 2, fontWeight: 500 }}>{viewTicket.phone || '—'}</Typography>
            
            <Typography variant="subtitle2" color="text.secondary">Event</Typography>
            <Typography variant="body2" sx={{ mb: 2, fontWeight: 500 }}>{viewTicket.eventTitle || viewTicket.eventSlug || '—'}</Typography>
            
            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle2" color="text.secondary">Reason</Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>{viewTicket.reason || '—'}</Typography>
            
            <Typography variant="subtitle2" color="text.secondary">Comment</Typography>
            <Typography variant="body2" sx={{ mb: 2, whiteSpace: 'pre-wrap', bgcolor: '#f8fafc', p: 1.5, borderRadius: 2, border: '1px solid #e2e8f0' }}>{viewTicket.comment || '—'}</Typography>
            
            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle2" color="text.secondary">Assignee</Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>{viewTicket.assignedToName || '—'}</Typography>

            <Typography variant="subtitle2" color="text.secondary">Created By</Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>{viewTicket.createdByName || '—'}</Typography>

            <Typography variant="subtitle2" color="text.secondary">Created At</Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>{formatDate(viewTicket.createdAt)}</Typography>
            
            <Typography variant="subtitle2" color="text.secondary">Status</Typography>
            <Box sx={{ mt: 0.5 }}>
              <StatusChip status={viewTicket.status || 'open'} label={statusLabel(viewTicket.status)} />
            </Box>
          </Box>
        ) : null}
      </Drawer>
    </div>
  );
};

export default SupportTickets;
